import { useEffect, useState } from "react";
import { useAppContext } from "../context/app/context";
import { useLocation, useNavigate } from "react-router-dom";
import { useValidateTokenLazyQuery } from "../graphql/generated/hooks";
import { setUser } from "../context/app/action";
import { LOCAL_STORAGE_KEYS } from "../common/constants";

const passingPaths = ["/login", "/invite"];

export function useAuthenticate() {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [validateToken] = useValidateTokenLazyQuery();
  const appContext = useAppContext();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const currentUser = appContext.state.user;
  const sessionToken = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
  const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  const sessionKey =
    sessionToken && refreshToken ? `${sessionToken}:${refreshToken}` : "";
  const [settledSessionKey, setSettledSessionKey] = useState<string | null>(
    null
  );
  const sessionPending =
    Boolean(sessionKey) && !currentUser && settledSessionKey !== sessionKey;
  useEffect(() => {
    let isMounted = true;

    if (currentUser) {
      if (location.pathname === "/login") {
        navigate("/", { replace: true });
      }
      return;
    }
    
    (async () => {
      try {
        const isPublicPath = passingPaths.find((rec) =>
          location.pathname.includes(rec)
        );

        // Read tokens fresh from localStorage inside useEffect to get latest values
        const sessionToken = localStorage.getItem(
          LOCAL_STORAGE_KEYS.SESSION_TOKEN
        );
        const refreshToken = localStorage.getItem(
          LOCAL_STORAGE_KEYS.REFRESH_TOKEN
        );

        // Allow access to public paths only when user is actually unauthenticated
        if (isPublicPath && (!sessionToken || !refreshToken)) {
          if (isMounted) {
            setLoading(false);
            setAuthenticated(true);
          }
          return;
        }

        // Redirect to login if no tokens are present
        if (!sessionToken || !refreshToken) {
          if (isMounted && location.pathname !== "/login") {
            navigate(`/login`);
          }
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        // Validate the current token
        // If token is expired, authLink will automatically refresh it and retry
        validateToken()
          .then((res) => {
            if (!res.data?.validateToken) {
              if (isMounted) {
                setSettledSessionKey(sessionKey);
                setLoading(false);
              }
              return;
            }

            const user = JSON.parse(res.data.validateToken);
            
            // Verify user has admin role
            if (!user) {
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
                setLoading(false);
              }
              return;
            }

            if (!user.roles?.includes("admin")) {
              alert("Access Denied: This portal is for administrators only.");
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
                setLoading(false);
              }
              return;
            }

            // Store user data in app context
            if (isMounted) {
              appContext.dispatch(
                setUser({
                  id: user.id,
                  username: user.username,
                  permissions: user.permissions,
                  context: user.context,
                })
              );
              setAuthenticated(true);
              setLoading(false);

              // If token is valid and user lands on /login, move to app shell directly
              if (location.pathname === "/login") {
                navigate("/", { replace: true });
                return;
              }
              
              // If user is on /403 page after successful auth, redirect to intended page or dashboard
              if (window.location.pathname === "/403") {
                const intendedPath = localStorage.getItem(LOCAL_STORAGE_KEYS.INTENDED_PATH) || "/dashboard";
                localStorage.removeItem(LOCAL_STORAGE_KEYS.INTENDED_PATH);
                
                // Set loop protection before redirecting to detect if this path is forbidden again
                if (intendedPath !== "/dashboard") {
                  sessionStorage.setItem("last_403_path", intendedPath);
                  sessionStorage.setItem("last_403_time", Date.now().toString());
                } else {
                  // Going to dashboard - clear protection
                  sessionStorage.removeItem("last_403_path");
                  sessionStorage.removeItem("last_403_time");
                }
                
                navigate(intendedPath);
              } else {
                // Not on /403, clear protection
                sessionStorage.removeItem("last_403_path");
                sessionStorage.removeItem("last_403_time");
              }
            }
          })
          .catch((error) => {
            // Only logout if it's a critical auth error, not a network issue
            const isAuthFailure = 
              error?.graphQLErrors?.some((err: any) => 
                err.extensions?.code === "UNAUTHENTICATED" ||
                err.extensions?.code === "UNAUTHORIZED_TOKEN_VALIDATION" ||
                err.extensions?.code === "FORBIDDEN"
              );

            if (isAuthFailure) {
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
              }
            }
            
            if (isMounted) {
              setSettledSessionKey(sessionKey);
              setLoading(false);
            }
          });
      } catch (e) {
        // Clear both tokens on unexpected errors
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        if (isMounted) {
          setLoading(false);
          navigate(`/login`);
        }
      }
    })();
    
    return () => {
      isMounted = false;
    };
    // Re-run when leaving /login so a new session is validated without a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentUser]);
  return {
    loading: loading || sessionPending,
    authenticated,
  };
}
